import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LayoutDashboard,
  Users,
  Shield,
  CreditCard,
  Store,
  MessageSquare,
  Siren,
  BellRing,
  FileText,
  Settings,
  ChevronDown,
  ChevronRight,
  LogOut,
  BanknoteIcon,
  ExternalLink,
  X,
} from "lucide-react";
import { breakWord } from "../../lib/utils";

function Sidebar({
  onLogout,
  role,
  isMobile = false,
  toggleMobileMenu = () => {},
}) {
  const location = useLocation();

  const hasPermission = (key) => {
    if (!role || !role.permissions) return false;
    return role.permissions.some(
      (perm) =>
        perm.key === key ||
        (perm.permission_key && perm.permission_key.includes(key))
    );
  };

  const getExpandedMenusFromPath = (path) => {
    const expanded = {};
    filteredMenuItems.forEach((item) => {
      if (item.subItems) {
        if (item.subItems.some((sub) => path.startsWith(sub.path))) {
          expanded[item.title] = true;
        }
      }
    });
    return expanded;
  };

  const menuItems = [
    {
      title: "Dashboard",
      icon: <LayoutDashboard className="h-5 w-5" />,
      path: "/",
      key: "dashboard",
    },
    {
      title: "User Management",
      icon: <Users className="h-5 w-5" />,
      subItems: [
        { title: "Users", path: "/users", key: "userManagement" },
        {
          title: "Admin-Users",
          path: "/admin-users",
          key: "adminUserManagement",
        },
      ],
      key: "userManagement",
    },
    {
      title: "Role Management",
      icon: <Shield className="h-5 w-5" />,
      path: "/roles",
      key: "roleManagement",
    },

    {
      title: (
        <>
          Subscription
          <br />
          Management
        </>
      ),
      icon: <CreditCard className="h-5 w-5" />,
      subItems: [
        {
          title: "Subscription Plans",
          path: "/subscriptions",
          key: "subscriptionManagement",
        },
        { title: "Subscribed Users", path: "/subscribed-user", key: "plans" },
      ],
    },
    {
      title: "Store",
      icon: <Store className="h-5 w-5" />,
      subItems: [
        { title: "Pets", path: "/pets", key: "pets" },
        { title: "Accessories", path: "/accessories", key: "accessories" },
        {
          title: "Sound",
          path: "/sound",
          key: "sound",
        },
        {
          title: "Add-On",
          path: "/pet-packages",
          key: "/pet-packages",
        },
        {
          title: "BG-Pattern",
          path: "/bg-patterns",
          key: "/bg-patterns",
        },
      ],
      key: "store",
    },
    {
      title: "Trigger",
      icon: <Siren className="h-5 w-5" />,
      path: "/trigger",
      key: "trigger",
    },
    {
      title: "Advertising",
      icon: <ExternalLink className="h-5 w-5" />,
      path: "/ads",
      key: "adsManagement",
    },
    // {
    //   title: "Voice Commands",
    //   icon: <MessageSquare className="h-5 w-5" />,
    //   path: "/voice-commands",
    //   key: "voiceCommands",
    // },
    {
      title: "CMS",
      icon: <FileText className="h-5 w-5" />,
      path: "/cms",
      key: "cmsPage",
    },
    // {
    //   title: "Theme Customization",
    //   icon: <Store className="h-5 w-5" />,
    //   subItems: [
    //     { title: "Sound", path: "/sound", key: "sound" },
    //     { title: "Pet", path: "/pet", key: "pet" },
    //   ],
    //   key: "themeCustomization",
    // },
    {
      title: "Contact Us Management",
      icon: <MessageSquare className="h-5 w-5" />,
      path: "/contact-us-management",
      key: "contactUsManagement",
    },
    {
      title: "Notification Management",
      icon: <BellRing className="h-5 w-5" />,
      path: "/notifications",
      key: "notificationManagement",
    },
    {
      title: "Payment Management",
      icon: <BanknoteIcon className="h-5 w-5" />,
      path: "/payments",
      key: "paymentmanagement",
    },
    {
      title: "Settings",
      icon: <Settings className="h-5 w-5" />,
      subItems: [
        { title: "Change Password", path: "/profile/change-password" },
        // {
        //   title: "Server Settings",
        //   path: "/settings/server",
        //   key: "serverSettings",
        // },
        // {
        //   title: "Email Templates",
        //   path: "/settings/email",
        //   key: "emailSettings",
        // },
      ],
    },
  ];

  const filteredMenuItems = menuItems
    .map((item) => {
      if (role?.name === "admin") {
        return item;
      }
      if (item.key && !hasPermission(item.key)) {
        return null;
      }
      if (item.subItems) {
        const filteredSubItems = item.subItems.filter((sub) => {
          if (sub.key) {
            return hasPermission(sub.key);
          }
          return true;
        });
        if (filteredSubItems.length === 0) {
          return null;
        }
        return { ...item, subItems: filteredSubItems };
      }
      return item;
    })
    .filter(Boolean);

  const [expandedMenus, setExpandedMenus] = useState(() =>
    getExpandedMenusFromPath(location.pathname)
  );
  const [selectedMenu, setSelectedMenu] = useState(location.pathname);

  const toggleSubMenu = (title) => {
    setExpandedMenus((prev) => ({ ...prev, [title]: !prev[title] }));
  };

  const isActive = (path) => location.pathname === path;

  const handleItemClick = (path, parentTitle = null) => {
    setSelectedMenu(path);
    if (parentTitle) {
      setExpandedMenus((prev) => ({ ...prev, [parentTitle]: true }));
    }
  };

  useEffect(() => {
    setSelectedMenu(location.pathname);
    setExpandedMenus((prev) => ({
      ...prev,
      ...getExpandedMenusFromPath(location.pathname),
    }));
  }, [location.pathname]);

  const user = JSON.parse(localStorage.getItem("authUser"));

  return (
    <aside
      className={`w-64 h-screen bg-white shadow-sm ${
        isMobile ? "block fixed inset-0 z-50" : "hidden md:block"
      }`}
    >
      <div className="h-full flex flex-col overflow-y-auto">
        <motion.div
          className="px-6 py-5 flex items-center border-b ml-5"
          initial={false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, type: "spring", stiffness: 300 }}
        >
          <img
            src="zuba-logo.png"
            className="w-[106px] h-[114px] object-cover object-center"
            style={{
              marginTop: "-40px",
              marginBottom: "-40px",
              marginLeft: "-20px",
              marginRight: "20px",
            }}
            alt="Logo"
          />
          <motion.span
            className="text-xs bg-primary-100 text-primary px-2 py-0.5 rounded ml-2 text-center"
            initial={false}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            whileHover={{
              backgroundColor: "rgba(224, 231, 255, 0.9)",
              scale: 1.05,
            }}
          >
            {role?.name || user?.user_type
              ? breakWord(user?.user_type)
              : "Admin User"}
          </motion.span>
          {isMobile && (
            <button
              className="ml-5 text-gray-500 hover:text-gray-700 focus:outline-none"
              onClick={toggleMobileMenu}
            >
              <X className="w-6 h-6" />
            </button>
          )}
        </motion.div>
        <div className="flex-1 overflow-y-auto py-2">
          <ul className="px-3 space-y-1">
            {filteredMenuItems.map((item, i) => {
              const isOpen = !!expandedMenus[item.title];
              return (
                <li key={i} className="space-y-1">
                  {item.subItems ? (
                    <>
                      <button
                        className={`flex items-center justify-between w-full px-3 py-2 text-sm rounded-md ${
                          isOpen
                            ? "bg-primary-50 text-primary font-medium"
                            : "text-gray-700"
                        }`}
                        onClick={() => toggleSubMenu(item.title)}
                      >
                        <div className="flex items-center">
                          <span
                            className="mr-3"
                            style={{
                              color: isOpen ? "#6366F1" : "#6B7280",
                            }}
                          >
                            {item.icon}
                          </span>
                          <span>{item.title}</span>
                        </div>
                        {isOpen ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </button>

                      {/* NO MOTION HERE—just static render */}
                      {isOpen && (
                        <ul className="pl-10 mt-1 space-y-1">
                          {item.subItems.map((sub, j) => (
                            <li key={j}>
                              <Link
                                to={sub.path}
                                className={`cursor-pointer block px-3 py-2 text-sm rounded-md ${
                                  isActive(sub.path)
                                    ? "text-primary font-medium"
                                    : "text-gray-700"
                                }`}
                                onClick={() =>
                                  handleItemClick(sub.path, item.title)
                                }
                              >
                                {sub.title}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  ) : (
                    <Link
                      to={item.path}
                      className={`cursor-pointer flex items-center px-3 py-2 text-sm rounded-md ${
                        isActive(item.path)
                          ? "text-primary font-medium"
                          : "text-gray-700"
                      }`}
                      onClick={() => handleItemClick(item.path)}
                    >
                      <span
                        className="mr-3"
                        style={{
                          color: isActive(item.path) ? "#6366F1" : "#6B7280",
                        }}
                      >
                        {item.icon}
                      </span>
                      <span>{item.title}</span>
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
        <div className="px-4 py-4 border-t">
          <button
            onClick={onLogout}
            className="flex items-center w-full px-3 py-2 text-sm text-gray-700 rounded-md hover:bg-gray-100"
          >
            <LogOut className="h-5 w-5 mr-3 text-gray-500" />
            <span>Logout</span>
          </button>
        </div>
      </div>
    </aside>
  );
}

export default Sidebar;
